"use client";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { type FieldValues } from "react-hook-form";
import ErrorMessage from "@/components/common/formElements/controlled/ErrorMessage";
import {
  useCheckboxFieldController,
  type UseCheckboxFieldControllerProps,
} from "@/components/common/formElements/hooks/useCheckboxFieldController";
import Checkbox, {
  type CheckboxProps,
} from "@/components/common/formElements/library/Checkbox";

type CheckboxPropsWithControllerProps<TFieldValues extends FieldValues> = Omit<
  CheckboxProps,
  "value" | "name" | "defaultValue" | "disabled" | "onChange"
> &
  UseCheckboxFieldControllerProps<TFieldValues>;

export interface CheckboxFieldProps<TFieldValues extends FieldValues>
  extends CheckboxPropsWithControllerProps<TFieldValues> {
  // "nativeDisabled" visually disables checkboxes without resetting the field value
  nativeDisabled?: boolean;
  errorMessageDisabled?: boolean;
  fullWidth?: boolean;
}

export default function CheckboxField<TFieldValues extends FieldValues>(
  props: CheckboxFieldProps<TFieldValues>,
) {
  const {
    name,
    value,
    multiple,
    onChange,
    defaultValue,
    rules,
    shouldUnregister,
    disabled,
    nativeDisabled,
    errorMessageDisabled,
    groupName,
    fullWidth,
    ...restCheckboxProps
  } = props;

  const {
    field,
    isChecked,
    onChangeField,
    required,
    formState: { errors },
    fieldState: { invalid: error },
  } = useCheckboxFieldController({
    name,
    value,
    multiple,
    onChange,
    defaultValue,
    rules,
    shouldUnregister,
    disabled,
    groupName,
  });

  return (
    <FormControl error={error} fullWidth={fullWidth}>
      <Checkbox
        name={field.name}
        inputRef={field.ref}
        value={value}
        disabled={nativeDisabled || disabled}
        required={required}
        checked={isChecked}
        onChange={onChangeField}
        {...restCheckboxProps}
      />
      {!errorMessageDisabled && error && (
        <FormHelperText>
          <ErrorMessage name={field.name} errors={errors} />
        </FormHelperText>
      )}
    </FormControl>
  );
}
